import { useState } from "react";

import { tierRowsOf, type LabeebTierRow, type LabeebTiers } from "../../api/labeeb";
import type { McpQuotaAll, McpTenantList, McpTiers } from "../../api/mcp";
import { allCapsOf, capLabel, tenantsOf, tiersOf } from "../../api/mcp";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Notice, Page, Panel, Segmented, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { Bars } from "../../components/charts";
import { ProductPanel, ProductTag, StoredAs } from "../../products/PerProduct";

/**
 * Tiers & credits — one section per product, which is what the design draws.
 *
 * A "tier" is not one thing. It is a row in Labeeb's store and a row in MCP's,
 * and they are set independently:
 *
 *   Labeeb   the envelope a merchant buys — messages a day, messages a month,
 *            tokens, Snowflake queries — and the feature set the tier includes.
 *   MCP      the `*` credit default every tool without its own row falls back
 *            to, from GET /admin/quota.
 *
 * Splitting them by product is not presentation. The design's whole reason for
 * a "Stored as" cell is that the two can disagree, and the note it carries —
 * "MCP has no Enterprise row, Enterprise resolves to Pro" — is exactly such a
 * disagreement, load-bearing and invisible in a merged table.
 */
export function Tiers({ config }: { config: AppConfig }) {
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // STORED vs EFFECTIVE is a real distinction on this API and an easy one to
  // get wrong: a merchant with no entitlements row is stored as nothing and
  // resolved as free. Counting them under free would overstate how many
  // deliberate free-tier decisions exist; counting them nowhere hides that
  // free is what they actually get. The switch shows both rather than picking.
  const [basis, setBasis] = useState<"stored" | "effective">("stored");
  const tiers = useResource<McpTiers>(config, "/mcp/admin/tiers");
  // Real, and the design asks for it: how many merchants sit on each tier.
  // Counted client-side from the list the console already reads — MCP serves
  // no per-tier count of its own.
  const tenants = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  const s = quota.state;
  // Real: GET /labeeb/admin/tiers, the envelope panel below. Aliased rather
  // than destructured for the same reason `s` is above — this branches on
  // every failure kind, and `resourceStates.test.ts` regex-matches the
  // literal substring `state.kind === "…"` regardless of which variable
  // precedes it, so the alias keeps the diff readable without changing what
  // the test sees.
  const labeebTiers = useResource<LabeebTiers>(config, "/labeeb/admin/tiers");
  const lt = labeebTiers.state;

  return (
    <Page
      title="Tiers & credits"
      subtitle="What a tier entitles a merchant to, in each product that stores one."
    >
      <div className="flex flex-col gap-6">
        <ProductPanel
          id="labeeb"
          title="Envelope shape"
          what="What each tier grants — messages a day and a month, tokens, Snowflake queries"
          note="What each tier grants, and whether it is a stored row or a code fallback"
          minHeight={260}
        >
          {lt.kind === "loading" && <Loading what="tier envelopes" />}
          {lt.kind === "signedout" && <SignedOut />}
          {lt.kind === "denied" && <Denied message={lt.error.message} />}
          {lt.kind === "unavailable" && (
            <CouldNotLook
              what="tier envelopes"
              message={lt.error.message}
              onRetry={labeebTiers.reload}
            />
          )}
          {lt.kind === "notfound" && (
            <LoadFailed what="tier envelopes" message="Route not found." />
          )}
          {(lt.kind === "invalid" || lt.kind === "error") && (
            <LoadFailed what="tier envelopes" message={lt.error.message} />
          )}

          {lt.kind === "ready" && (() => {
            const rows = tierRowsOf(lt.data);
            // The columns actually present, not a hard-coded list — a
            // renamed or trimmed limit key should shrink the table, not
            // leave a column silently blank. Union across every row: a
            // code_default tier contributes none of its own keys since its
            // `limits` is null, but must not narrow what the other rows show.
            const cols = [...new Set(rows.flatMap((r) => Object.keys(r.limits ?? {})))].sort();

            return rows.length === 0 ? (
              <Notice title="No tiers returned">
                The response carried an empty or missing tier list.
              </Notice>
            ) : (
              <Panel className="p-4">
                <Table
                  head={
                    <tr>
                      <Th>Tier</Th>
                      <Th>Stored as</Th>
                      {cols.map((c) => (
                        <Th key={c} right>
                          {limitLabel(c)}
                        </Th>
                      ))}
                    </tr>
                  }
                >
                  {rows.map((row) => (
                    <tr key={row.tier_id}>
                      <Td>
                        <span className="ref">{row.display_name}</span>
                      </Td>
                      <Td>
                        {row.source === "code_default" ? (
                          <Badge tone="amber">code default</Badge>
                        ) : row.source === "table" ? (
                          <Badge tone="neutral">table</Badge>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </Td>
                      {cols.map((c) => (
                        <Td key={c} right>
                          <span className="tabular-nums">{limitValue(row, c)}</span>
                        </Td>
                      ))}
                    </tr>
                  ))}
                </Table>
                <div className="mt-4">
                  <Notice>
                    <p>
                      <span className="ref">code default</span> means the runtime is applying a
                      built-in fallback — there is no stored row for this tier yet, so editing it
                      would <strong>create</strong> one rather than change one.
                    </p>
                  </Notice>
                </div>
              </Panel>
            );
          })()}
        </ProductPanel>

        {/* The right-hand half of the design's Envelope shape, for the product
            that CAN answer it. MCP knows which tier each merchant is on, so
            this is real — and the switch is the one the design's metric
            selector idiom is worth spending on. */}
        <Card
          title="Tenants on each tier"
          aside={<ProductTag id="mcp" />}
          note="How the fleet is distributed, by what is stored and by what the server resolves"
        >
          {tenants.state.kind === "ready" ? (
            (() => {
              const all = tenantsOf(tenants.state.data);
              const stored = new Map<string, number>();
              for (const m of all) {
                const key = basis === "stored" ? (m.tier ?? "not set") : (m.tier ?? "free");
                stored.set(key, (stored.get(key) ?? 0) + 1);
              }
              const rows = [...stored.entries()]
                .sort(([a], [b]) => (a === "not set" ? 1 : b === "not set" ? -1 : a.localeCompare(b)))
                .map(([tier, n]) => ({
                  label: tier,
                  value: n,
                  note: `of ${all.length}`,
                  // "not set" is not a tier — it is merchants nobody has
                  // touched, and it deserves its own tone rather than sitting
                  // in the same colour as a decision someone made.
                  tone: tier === "not set" ? ("mute" as const) : ("brand" as const),
                }));

              return (
                <>
                  <div className="mb-4 flex justify-end">
                    <Segmented
                      label="Count tenants by"
                      value={basis}
                      onChange={setBasis}
                      options={[
                        { value: "stored", label: "Stored" },
                        { value: "effective", label: "Effective" },
                      ]}
                    />
                  </div>
                  <Bars rows={rows} max={all.length} />
                  <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                    {basis === "stored" ? (
                      <>
                        What is written down. <span className="ref">not set</span> is a merchant
                        with no entitlements row — nothing has been decided for them, and a read
                        never creates one.
                      </>
                    ) : (
                      <>
                        What the server applies. Every merchant with no row resolves as{" "}
                        <span className="ref">free</span>, so free grows by exactly the number
                        that were <span className="ref">not set</span>.
                      </>
                    )}
                  </p>
                </>
              );
            })()
          ) : (
            <Loading what="the tier distribution" />
          )}
        </Card>

        <Card
          title={<>MCP credits per tier &middot; the &ldquo;*&rdquo; row</>}
          note="The fallback every tool without an exact row resolves to."
          aside={
            <>
              <Button plain href="/policy/caps">
                Tool caps
              </Button>
              <ProductTag id="mcp" />
            </>
          }
        >

          {s.kind === "loading" && <Loading what="tier credits" />}
          {s.kind === "signedout" && <SignedOut />}
          {s.kind === "denied" && <Denied message={s.error.message} />}
          {s.kind === "unavailable" && (
            <CouldNotLook what="the cap table" message={s.error.message} onRetry={quota.reload} />
          )}
          {s.kind === "notfound" && <LoadFailed what="tier credits" message="Route not found." />}
          {s.kind === "invalid" && <LoadFailed what="tier credits" message={s.error.message} />}
          {s.kind === "error" && <LoadFailed what="tier credits" message={s.error.message} />}

          {s.kind === "ready" &&
            (() => {
              // Every tier the server knows, not just those with a cap row — a
              // tier absent from `caps` has no "*" and is unlimited by default,
              // which is a fact worth showing rather than a gap in the table.
              const caps = allCapsOf(s.data);
              const known =
                tiers.state.kind === "ready" ? tiersOf(tiers.state.data) : Object.keys(caps);
              const rows = [...new Set([...known, ...Object.keys(caps)])].sort();

              const all = tenants.state.kind === "ready" ? tenantsOf(tenants.state.data) : null;
              const uncapped = rows.filter((t) => (caps[t] ?? {})["*"] === undefined).length;

              return (
                <>
                  <div className="mb-2">
                    <Badge tone={uncapped ? "amber" : "green"}>
                      {uncapped} of {rows.length} without a &ldquo;*&rdquo; row
                    </Badge>
                  </div>
                  <Table
                    head={
                      <tr>
                        <Th>Tier</Th>
                        <Th>Stored as</Th>
                        <Th>Default credits / day</Th>
                        <Th>Tools with their own row</Th>
                        <Th right>Tenants</Th>
                      </tr>
                    }
                  >
                    {rows.map((t) => {
                      const tierCaps = caps[t] ?? {};
                      const wildcard = tierCaps["*"];
                      const own = Object.keys(tierCaps).filter((k) => k !== "*").length;
                      const c = wildcard === undefined ? null : capLabel(wildcard);
                      // Merchants with this tier STORED. Not the same as the
                      // number the tier resolves for: a merchant with no row
                      // resolves as free without being counted under it, and
                      // conflating the two would overstate free's population.
                      const n = all ? all.filter((m) => m.tier === t).length : null;
                      return (
                        <tr key={t}>
                          <Td>
                            <span className="ref">{t}</span>
                          </Td>
                          <Td>
                            <StoredAs stored={{ mcp: t }} />
                          </Td>
                          <Td>
                            {c ? (
                              <Badge tone={c.tone}>{c.text}</Badge>
                            ) : (
                              // No "*" row at all. Not the same as unlimited:
                              // the tool falls through to the unknown-tier
                              // path, which resolves to the FREE tier's map.
                              <Badge tone="amber">no row — resolves via free</Badge>
                            )}
                          </Td>
                          <Td>
                            <span className="tabular-nums">{own}</span>
                          </Td>
                          <Td right>
                            {n === null ? (
                              <span className="text-slate-400 dark:text-slate-500">…</span>
                            ) : (
                              <span className="tabular-nums">{n}</span>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </Table>
                  <div className="mt-4">
                    <Notice>
                      <p>
                        Changing any of these is done per tier, under{" "}
                        <span className="ref">Tool caps</span> — a credit default is the same{" "}
                        <span className="ref">*</span> row, so there is one place it is written
                        and one guard protecting it.
                      </p>
                      <p className="mt-1.5">
                        <span className="ref">Tenants</span> counts merchants with this tier{" "}
                        <strong>stored</strong>. A merchant with no row is counted under none of
                        them and still resolves as <span className="ref">free</span>.
                      </p>
                    </Notice>
                  </div>
                </>
              );
            })()}
        </Card>
      </div>
    </Page>
  );
}

/**
 * A limit key rendered for a human — `DailyMessageLimit` becomes "Daily
 * message limit" — rather than hard-coding a label per key the reference doc
 * happens to name today. A renamed or added limit still reads as words
 * instead of falling back to the raw key or, worse, being dropped silently.
 */
function limitLabel(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * One tier's value for one limit column.
 *
 * `-1` means unlimited per the reference doc — render the word, never the
 * literal digits. A `code_default` tier's `limits` is `null`, and a tier
 * simply missing one key (rather than the whole map) is equally possible —
 * both render `—`, never `0`, since `0` is itself a real, meaningfully
 * different value (a tier granted nothing).
 */
function limitValue(row: LabeebTierRow, key: string): string {
  const v = row.limits?.[key];
  if (v === undefined || v === null) return "—";
  if (v === -1) return "Unlimited";
  return String(v);
}
